const dealerInventoryDataEl = document.getElementById('dealer-inventory-data');
const dealerLocations = dealerInventoryDataEl ? JSON.parse(dealerInventoryDataEl.textContent).locations : [];

async function fetchDealerStock(variantId, locations) {
  const response = await fetch('https://us-central1-music-connoisseur-360.cloudfunctions.net/getDealerInventory/dealer-inventory', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ variantId, locations })
  });
  const result = await response.json();
  // Sum up all available stock
  return (result.inventoryLevels || []).reduce((sum, lvl) => sum + (lvl.available || 0), 0);
}

const ENYA_LIVEMUSIC_COLLECTION_TITLES = [
  'ENYA Inspire / Sonic',
  'ENYA Nova Go Acoustic Guitar',
  'ENYA Nova U',
  'ENYA X Series',
  'ENYA NEXG Series',
];

function normalizeCollectionTitle(title) {
  return String(title || '').trim().toLowerCase();
}

const EFNOTE_ELECTRONIC_DRUM_KITS_COLLECTION_TITLE = 'EFNOTE Electronic Drum Kits';

function isInCollection(collectionTitles = [], requiredTitle) {
  const normalizedSet = new Set((collectionTitles || []).map(normalizeCollectionTitle));
  return normalizedSet.has(normalizeCollectionTitle(requiredTitle));
}

function getEnyaLiveMusicCollectionKey(collectionTitles = []) {
  const normalizedSet = new Set((collectionTitles || []).map(normalizeCollectionTitle));
  for (const allowedTitle of ENYA_LIVEMUSIC_COLLECTION_TITLES) {
    if (normalizedSet.has(normalizeCollectionTitle(allowedTitle))) return allowedTitle;
  }
  return null;
}

class DealerCartItems extends HTMLElement {
  constructor() {
    super();
    this.cartKey = 'dealerCart';
    this.cartItemsContainer = null;
    this.cartWarnings = null;
    this.cartContents = null;
    this._isUpdating = false;  // Add flag to track updates
    this.loadingOverlay = null;
    this._lastRequestTime = 0;  // Track last API request time
    this._minRequestInterval = 1000; // Minimum 1 second between requests
    this._pendingRequests = new Map(); // Track pending requests by variant ID
  }

  connectedCallback() {
    this.cartItemsContainer = this.querySelector('#dealer-cart-items');
    this.cartWarnings = this.querySelector('.cart__warnings');
    this.cartContents = this.querySelector('.cart__contents');
    this.createLoadingOverlay();
    this.renderCart();
  }

  createLoadingOverlay() {
    // Create loading overlay if it doesn't exist
    if (!this.loadingOverlay) {
      this.loadingOverlay = document.createElement('div');
      this.loadingOverlay.className = 'dealer-cart-loading-overlay';
      this.loadingOverlay.innerHTML = `
        <div class="dealer-cart-loading-content">
          <div class="dealer-cart-loading-spinner"></div>
          <p>Updating cart...</p>
          <div class="dealer-cart-loading-progress"></div>
        </div>
      `;
      this.loadingOverlay.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.7);
        display: none;
        justify-content: center;
        align-items: center;
        z-index: 9999;
        backdrop-filter: blur(3px);
        opacity: 0;
        transition: opacity 0.3s ease-in-out;
      `;
      
      const loadingContent = this.loadingOverlay.querySelector('.dealer-cart-loading-content');
      loadingContent.style.cssText = `
        background: white;
        padding: 2.5rem;
        border-radius: 12px;
        text-align: center;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
        max-width: 320px;
        transform: scale(0.9);
        transition: transform 0.3s ease-in-out;
      `;
      
      const spinner = this.loadingOverlay.querySelector('.dealer-cart-loading-spinner');
      spinner.style.cssText = `
        width: 48px;
        height: 48px;
        border: 4px solid #f3f3f3;
        border-top: 4px solid #008060;
        border-radius: 50%;
        animation: dealer-cart-spin 1s linear infinite;
        margin: 0 auto 1.5rem;
      `;
      
      const text = this.loadingOverlay.querySelector('p');
      text.style.cssText = `
        margin: 0 0 1rem 0;
        color: #333;
        font-size: 1.1rem;
        font-weight: 600;
      `;
      
      const progress = this.loadingOverlay.querySelector('.dealer-cart-loading-progress');
      progress.style.cssText = `
        width: 100%;
        height: 4px;
        background: #f0f0f0;
        border-radius: 2px;
        overflow: hidden;
        margin-top: 1rem;
      `;
      
      // Add CSS animation
      if (!document.querySelector('#dealer-cart-loading-styles')) {
        const style = document.createElement('style');
        style.id = 'dealer-cart-loading-styles';
        style.textContent = `
          @keyframes dealer-cart-spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          .dealer-cart-loading-overlay.show {
            opacity: 1;
          }
          
          .dealer-cart-loading-overlay.show .dealer-cart-loading-content {
            transform: scale(1);
          }
          
          .dealer-cart-loading-progress::after {
            content: '';
            display: block;
            width: 30%;
            height: 100%;
            background: #008060;
            border-radius: 2px;
            animation: dealer-cart-progress 2s ease-in-out infinite;
          }
          
          @keyframes dealer-cart-progress {
            0% { transform: translateX(-100%); }
            50% { transform: translateX(200%); }
            100% { transform: translateX(-100%); }
          }
        `;
        document.head.appendChild(style);
      }
      
      document.body.appendChild(this.loadingOverlay);
    }
  }

  showLoading() {
    if (this.loadingOverlay) {
      // Clear any existing timeout
      if (this._loadingTimeout) {
        clearTimeout(this._loadingTimeout);
      }
      
      // Show loading immediately for operations that are likely to take time
      this.loadingOverlay.style.display = 'flex';
      setTimeout(() => {
        this.loadingOverlay.classList.add('show');
      }, 10);
    }
  }

  hideLoading() {
    if (this.loadingOverlay) {
      this.loadingOverlay.classList.remove('show');
      // Wait for the transition to complete before hiding
      setTimeout(() => {
        this.loadingOverlay.style.display = 'none';
      }, 300);
    }
  }

  showLoadingWithDelay(delay = 200) {
    if (this.loadingOverlay) {
      // Clear any existing timeout
      if (this._loadingTimeout) {
        clearTimeout(this._loadingTimeout);
      }
      
      // Show loading after a delay to avoid flickering for quick operations
      this._loadingTimeout = setTimeout(() => {
        this.showLoading();
      }, delay);
    }
  }

  cancelLoadingDelay() {
    if (this._loadingTimeout) {
      clearTimeout(this._loadingTimeout);
      this._loadingTimeout = null;
    }
  }

  canMakeRequest(variantId = null) {
    const now = Date.now();
    const timeSinceLastRequest = now - this._lastRequestTime;
    
    // Check if we're within the minimum interval
    if (timeSinceLastRequest < this._minRequestInterval) {
      this.scheduleRateLimitReset(); // Schedule reset for future
      return false;
    }
    
    // Check if there's already a pending request for this variant
    if (variantId && this._pendingRequests.has(variantId)) {
      return false;
    }
    
    return true;
  }

  showRateLimitMessage() {
    // Update loading message to show rate limit warning
    if (this.loadingOverlay) {
      const textElement = this.loadingOverlay.querySelector('p');
      if (textElement) {
        textElement.textContent = 'Please wait... Too many requests.';
        textElement.style.color = '#e74c3c'; // Red color for warning
      }
    }
  }

  resetLoadingMessage() {
    // Reset loading message back to normal
    if (this.loadingOverlay) {
      const textElement = this.loadingOverlay.querySelector('p');
      if (textElement) {
        textElement.textContent = 'Updating cart...';
        textElement.style.color = '#333'; // Reset to normal color
      }
    }
  }

  clearRateLimitState() {
    // Clear pending requests and reset rate limit state
    this._pendingRequests.clear();
    this._lastRequestTime = 0;
  }

  scheduleRateLimitReset() {
    // Clear rate limit state after 5 seconds of inactivity
    setTimeout(() => {
      this.clearRateLimitState();
    }, 5000);
  }

  getCart() {
    try {
      const cart = JSON.parse(localStorage.getItem(this.cartKey)) || [];
      console.log('[DEALER CART DEBUG] Retrieved cart from localStorage:', cart.map(item => ({
        title: item.title,
        productType: item.productType,
        vendor: item.vendor
      })));
      return cart;
    } catch (e) {
      console.error('Error reading cart from localStorage', e);
      return [];
    }
  }

  setCart(cart, recalculateDiscounts = true) {
    try {
      if (recalculateDiscounts) {
        // Always set original_price and price for each item
        const customerTags = this.getCustomerTags ? this.getCustomerTags() : [];
        cart.forEach(item => {
          if (typeof item.original_price !== 'number') {
            item.original_price = item.price;
          }
          // Calculate discount
          const { discount } = this.getDealerDiscount(item, customerTags);
          // Set price to discounted price if discount applies, else original_price
          item.price = discount > 0
            ? Math.round(item.original_price * (1 - discount / 100))
            : item.original_price;
        });
      }
      if (cart.length === 0) {
        localStorage.removeItem(this.cartKey);
      } else {
        localStorage.setItem(this.cartKey, JSON.stringify(cart));
        // console.log('Saved cart to localStorage:', cart);
      }
    } catch (e) {
      console.error('Error saving cart to localStorage', e);
    }
  }

  getDealerDiscount(item, customerTags) {
    let discount = 0;
    let nextTierMessage = '';
    const vendor = item.vendor;
    const collections = item.collections || [];
    const isCyberGCollection =
      Array.isArray(collections) && collections.some((c) => String(c).toLowerCase().includes('cyber-g'));
    let totalQty;
    
    // Check if this is an accessory
    // First try to use productType, if null/undefined, fall back to title-based detection
    let isAccessory = false;
    if (item.productType) {
      isAccessory = item.productType.toLowerCase() === 'accessories';
    } else {
      // Fallback: Check if title contains accessory-related keywords
      const accessoryKeywords = ['stand', 'strap', 'case', 'bag', 'pick', 'tuner', 'capo', 'strings', 'cable', 'adapter', 'holder', 'mount', 'bracket', 'stand'];
      isAccessory = accessoryKeywords.some(keyword => 
        item.title.toLowerCase().includes(keyword.toLowerCase())
      );
    }
    
    // Debug logging for product type detection - REMOVED TO REDUCE CONSOLE SPAM
    
    // Special handling for Cyber-G items (must come before general Enya Music check)
    if (vendor === 'Enya Music' && item.title.toLowerCase().includes('cyber-g')) {
      totalQty = item.total_cyber_g_qty || (item.quantity + (item.cumulative_quantity || 0));
    } else if (vendor === 'LAVA MUSIC' && item.title.toLowerCase().includes('genie')) {
      totalQty = item.total_genie_qty || (item.quantity + (item.cumulative_quantity || 0));
    } else if (["Enya Music", "Valeton", "Sonicake", "SIRE"].includes(vendor)) {
      totalQty = item.total_vendor_qty || 0;
    } else {
      const qty = item.quantity;
      const cumulativeQty = item.cumulative_quantity || 0;
      totalQty = qty + cumulativeQty;
    }

    // List of vendors that should not receive discounts
    const noDiscountVendors = [
      'Music Connoisseur', 'MUSO', 'Positive Grid', 'DV Mark', 'Markbass', 'JOYO', 
      'Neural DSP', 'Flanger', 'Swiff Audio', 'Artesia', 'Natasha', 'Rainie', 
      'Cole Clark', 'T Guitar', 'DK', 'Epiphone', 'Naga', 'Baldwin', 'EART', 
      'FBAGS', 'Galli', 'Hagstrom', 'ICON', 'Jinchuan', 'Kirlin', 'KZ', 
      'Martinez', 'Musedo', 'NUX', 'Prefox', 'Relish', 'Shijie', 'Stringjoy', 'Yida'
    ];

    // Return no discount if vendor is in the no-discount list
    if (noDiscountVendors.includes(vendor)) {
      return { discount: null, nextTierMessage: null };
    }

    // LAVA MUSIC
    if (vendor === 'LAVA MUSIC') {
      // Special handling for LAVA Studio - 20% discount, no cumulative needed
      if (item.title.toLowerCase().includes('studio')) {
        discount = 20;
      } else if (collections.includes('Lava Me Play') || collections.includes('Lava Me Air')) {
        discount = 20;
      } else if (collections.includes('Lava Me 4')) {
        discount = 25;
      } else if (collections.includes('Lava Me 3')) {
        discount = 30;
      } else if (item.title.toLowerCase().includes('genie')) {
        if (totalQty >= 10) {
          discount = 30;
        } else if (totalQty >= 4) {
          discount = 20;
          if (totalQty < 10) {
            nextTierMessage = `Add ${10 - totalQty} more for 30% OFF`;
          }
        } else {
          discount = 10;
          nextTierMessage = `Add ${4 - totalQty} more for 20% OFF`;
        }
      }
    }
    // SIRE
    else if (vendor === 'SIRE') {
      // console.log('[SIRE DEBUG] Item:', {
      //   title: item.title,
      //   original_price: item.original_price,
      //   compare_at_price: item.compare_at_price,
      //   using_compare_at: item.compare_at_price && item.compare_at_price > item.original_price
      // });
      
      // For SIRE, use compare at price if available
      if (item.compare_at_price && item.compare_at_price > item.original_price) {
        item.original_price = item.compare_at_price;
        // console.log('[SIRE DEBUG] Using compare_at_price as base:', item.compare_at_price);
      }
      
      if (totalQty >= 4) {
        discount = 30;
      } else if (totalQty >= 2) {
        discount = 20;
        if (totalQty < 4) {
          nextTierMessage = `Add ${4 - totalQty} more for 30% OFF`;
        }
      } else {
        discount = 10;
        nextTierMessage = `Add ${2 - totalQty} more for 20% OFF`;
      }
    }
    // Enya Music (including Cyber-G)
    else if (vendor === 'Enya Music') {
      const isDealerLiveMusic =
        Array.isArray(customerTags) &&
        customerTags.includes('Dealer') &&
        customerTags.includes('Live Music');
      const enyaCollectionKey = isDealerLiveMusic ? getEnyaLiveMusicCollectionKey(collections) : null;

      // Special handling for Cyber-G collection
      if (isCyberGCollection) {
        if (totalQty >= 10) {
          discount = 30;
        } else if (totalQty >= 4) {
          discount = 20;
          if (totalQty < 10) {
            nextTierMessage = `Add ${10 - totalQty} more for 30% OFF`;
          }
        } else {
          discount = 10;
          nextTierMessage = `Add ${4 - totalQty} more for 20% OFF`;
        }
      } else {
        // Regular Enya Music products (excluding Cyber-G)
        // For accessories, use the discount rate based on main products quantity
        if (isAccessory) {
          // Dealer + Live Music: collection-scoped tiers for the 5 specified ENYA collections
          if (isDealerLiveMusic && enyaCollectionKey && typeof item.total_enya_collection_qty === 'number') {
            const mainProductsQty = item.total_enya_collection_qty;
            if (mainProductsQty >= 12) {
              discount = 35;
            } else if (mainProductsQty >= 4) {
              discount = 30;
              if (mainProductsQty < 12) {
                nextTierMessage = `Add ${12 - mainProductsQty} more guitar(s) for 35% OFF`;
              }
            } else if (mainProductsQty >= 2) {
              discount = 20;
              if (mainProductsQty < 4) {
                nextTierMessage = `Add ${4 - mainProductsQty} more guitar(s) for 30% OFF`;
              }
            } else {
              discount = 10;
              nextTierMessage = `Add ${2 - mainProductsQty} more guitar(s) for 20% OFF`;
            }

          // Special dealer tags with 35% off for 12+ units (legacy behavior)
          } else if (customerTags.some(tag => ['Live Music', 'Music Bliss', 'Kuantan Music', 'JZ'].includes(tag))) {
            // For accessories, use the main products quantity (totalQty already excludes accessories)
            const mainProductsQty = totalQty;
            if (mainProductsQty >= 12) {
              discount = 35;
            } else if (mainProductsQty >= 4) {
              discount = 30;
              if (mainProductsQty < 12) {
                nextTierMessage = `Add ${12 - mainProductsQty} more guitar(s) for 35% OFF`;
              }
            } else if (mainProductsQty >= 2) {
              discount = 20;
              if (mainProductsQty < 4) {
                nextTierMessage = `Add ${4 - mainProductsQty} more guitar(s) for 30% OFF`;
              }
            } else {
              discount = 10;
              nextTierMessage = `Add ${2 - mainProductsQty} more guitar(s)s for 20% OFF`;
            }
          } else {
            // For accessories, use the main products quantity (totalQty already excludes accessories)
            const mainProductsQty = totalQty;
            if (mainProductsQty >= 4) {
              discount = 30;
            } else if (mainProductsQty >= 2) {
              discount = 20;
              if (mainProductsQty < 4) {
                nextTierMessage = `Add ${4 - mainProductsQty} more main products for 30% OFF`;
              }
            } else {
              discount = 10;
              nextTierMessage = `Add ${2 - mainProductsQty} more main products for 20% OFF`;
            }
          }
        } else {
          // Regular Enya Music products (excluding Cyber-G and accessories)
          // Dealer + Live Music: collection-scoped tiers for the 5 specified ENYA collections
          if (isDealerLiveMusic && enyaCollectionKey && typeof item.total_enya_collection_qty === 'number') {
            const mainProductsQty = item.total_enya_collection_qty;
            if (mainProductsQty >= 12) {
              discount = 35;
            } else if (mainProductsQty >= 4) {
              discount = 30;
              if (mainProductsQty < 12) {
                nextTierMessage = `Add ${12 - mainProductsQty} more for 35% OFF`;
              }
            } else if (mainProductsQty >= 2) {
              discount = 20;
              if (mainProductsQty < 4) {
                nextTierMessage = `Add ${4 - mainProductsQty} more for 30% OFF`;
              }
            } else {
              discount = 10;
              nextTierMessage = `Add ${2 - mainProductsQty} more for 20% OFF`;
            }

          // Special dealer tags with 35% off for 12+ units (legacy behavior)
          } else if (customerTags.some(tag => ['Live Music', 'Music Bliss', 'Kuantan Music', 'JZ'].includes(tag))) {
            // For both main products and accessories, use the main products quantity (totalQty already excludes accessories)
            const mainProductsQty = totalQty;
            if (mainProductsQty >= 12) {
              discount = 35;
            } else if (mainProductsQty >= 4) {
              discount = 30;
              if (mainProductsQty < 12) {
                nextTierMessage = `Add ${12 - mainProductsQty} more for 35% OFF`;
              }
            } else if (mainProductsQty >= 2) {
              discount = 20;
              if (mainProductsQty < 4) {
                nextTierMessage = `Add ${4 - mainProductsQty} more for 30% OFF`;
              }
            } else {
              discount = 10;
              nextTierMessage = `Add ${2 - mainProductsQty} more for 20% OFF`;
            }
          } else {
            const mainProductsQty = totalQty;
            if (mainProductsQty >= 4) {
              discount = 30;
            } else if (mainProductsQty >= 2) {
              discount = 20;
              if (mainProductsQty < 4) {
                nextTierMessage = `Add ${4 - mainProductsQty} more for 30% OFF`;
              }
            } else {
              discount = 10;
              nextTierMessage = `Add ${2 - mainProductsQty} more for 20% OFF`;
            }
          }
        }
      }
    }
    // Sonicake
    else if (vendor === 'Sonicake' && customerTags.includes('Music Outlet')) {
      if (totalQty >= 8) {
        discount = 35;
      } else if (totalQty >= 4) {
        discount = 30;
        if (totalQty < 8) {
          nextTierMessage = `Add ${8 - totalQty} more for 35% OFF`;
        }
      }
    }
    // Valeton
    else if (vendor === 'Valeton') {
      const productTitle = item.title.toLowerCase();
      // Special handling for GP-5/GP-50/GP-150/GP-180 (20% discount, no MOQ)
      if (productTitle.includes('gp-5') || productTitle.includes('gp-50') || productTitle.includes('gp-150') || productTitle.includes('gp-180')) {
        discount = 20;
        return { discount, nextTierMessage: '' };
      }
      // Other Valeton products: vendor-level quantity (can mix models)
      // 1 unit: 10%, 2-3 units: 20%, 4+ units: 30%, 8+ units: 35% (Music Outlet only)
      if (customerTags.includes('Music Outlet')) {
        if (totalQty >= 8) {
          discount = 35;
        } else if (totalQty >= 4) {
          discount = 30;
          if (totalQty < 8) {
            nextTierMessage = `Add ${8 - totalQty} more for 35% OFF`;
          }
        } else if (totalQty >= 2 && totalQty <= 3) {
          discount = 20;
          if (totalQty < 4) {
            nextTierMessage = `Add ${4 - totalQty} more for 30% OFF`;
          }
        } else {
          discount = 10;
          nextTierMessage = `Add ${2 - totalQty} more for 20% OFF`;
        }
      } else {
        if (totalQty >= 4) {
          discount = 30;
        } else if (totalQty >= 2 && totalQty <= 3) {
          discount = 20;
          if (totalQty < 4) {
            nextTierMessage = `Add ${4 - totalQty} more for 30% OFF`;
          }
        } else {
          discount = 10;
          nextTierMessage = `Add ${2 - totalQty} more for 20% OFF`;
        }
      }
    }
    // EFNOTE
    else if (vendor === 'EFNOTE') {
      discount = 30;
      nextTierMessage = '';
    }
    // Kurzweil
    else if (vendor === 'Kurzweil') {
      discount = 30;
      nextTierMessage = '';
    }
    // Default
    else {
      if (totalQty >= 4) {
        discount = 30;
      } else if (totalQty >= 2) {
        discount = 20;
        if (qty < 4) {
          nextTierMessage = `Add ${4 - totalQty} more for 30% OFF`;
        }
      } else {
        discount = 10;
        nextTierMessage = `Add ${2 - totalQty} more for 20% OFF`;
      }
    }

    // Debug logging for final discount calculation - REMOVED TO REDUCE CONSOLE SPAM
    
    return { discount, nextTierMessage };
  }

  getDealerCartRowHTML(item, index) {
    const customerTags = this.getCustomerTags ? this.getCustomerTags() : [];
    const { discount, nextTierMessage } = this.getDealerDiscount(item, customerTags);
    
    // For SIRE, use compare_at_price as the base price if available
    let originalPrice = item.original_price;
    if (item.vendor === 'SIRE' && item.compare_at_price && item.compare_at_price > item.original_price) {
      // console.log('[SIRE CART DEBUG] Using compare_at_price:', {
      //   title: item.title,
      //   original_price: item.original_price,
      //   compare_at_price: item.compare_at_price,
      //   new_base_price: item.compare_at_price
      // });
      originalPrice = item.compare_at_price;
    }
    
    const discountedPrice = discount > 0 ? Math.round(originalPrice * (1 - discount / 100)) : originalPrice;
    const lineTotal = discountedPrice * item.quantity;
    const originalLineTotal = originalPrice * item.quantity;
    let etaMsg = '';
    // if (item.latestDealerStock !== undefined && item.quantity > item.latestDealerStock) {
    //   const shortage = item.quantity - item.latestDealerStock;
    //   etaMsg = `<div class='dealer-eta-msg' style='color: #008060; font-weight: 500; margin-top: 0.5em;'>${shortage} units will be available in next upcoming shipment, ETA 6 weeks</div>`;
    // }

    // Add data attributes for collections and vendor
    const dataAttributes = `
      data-variant-id="${item.variantId}"
      data-vendor="${item.vendor || ''}"
      data-collections="${item.collections ? item.collections.join(',') : ''}"
    `;

    const isDealer = this.getCustomerTags && this.getCustomerTags().includes('Dealer');
    const maxAttr = isDealer ? '' : `max="${item.dealer_stock || 999}"`;

    //console.log('[SIRE CART DEBUG] Final prices:', {
    //  title: item.title,
    //  originalPrice,
    //  discountedPrice,
    //  lineTotal,
    //  originalLineTotal,
    //  discount
  //  });

    return `
      <tr class="cart-item" ${dataAttributes}>
        <td class="cart-item__product">
          <div class="cart-item__product-flex" style="display: flex; align-items: center; gap: 1.5rem;">
            <a href="${item.url}" style="display: block;">
              <img
                class="cart-item__image"
                src="${item.image}"
                alt="${item.title}"
                loading="lazy"
                width="80"
                height="80"
                style="border-radius: 6px; border: 1px solid #eee; object-fit: cover; display: block;"
              >
            </a>
            <div class="cart-item__details">
              <a href="${item.url}" class="cart-item__name h4 break" style="font-weight: 500; font-size: 1.1rem; color: #222; text-decoration: none;">
                ${item.title}
              </a>
              <div class="cart-item__price-wrapper">
                ${discount > 0 ? `
                  <div class='cart-item__discounted-prices'>
                    ${discount !== null ? `<span class='discount-badge'>-${discount}% OFF</span>` : ''}
                    ${nextTierMessage ? `<span class='next-tier-message'>${nextTierMessage}</span>` : ''}
                    <s class='cart-item__old-price'>RM${(originalPrice / 100).toLocaleString(undefined, {minimumFractionDigits: 2})} MYR</s>
                    <span class='cart-item__final-price'>RM${(discountedPrice / 100).toLocaleString(undefined, {minimumFractionDigits: 2})} MYR</span>
                  </div>
                ` : `
                  <span class='price price--end'>RM${(originalPrice / 100).toLocaleString(undefined, {minimumFractionDigits: 2})} MYR</span>
                `}
              </div>
            </div>
          </div>
        </td>
        <td class="cart-item__quantity cart-item__quantity--info cart-items__heading--wide">
          <div class="cart-item__quantity-wrapper quantity-popover-wrapper">
            <label class="visually-hidden" for="Quantity-${index + 1}">Quantity</label>
            <div class="quantity-popover-container">
              <quantity-input class="quantity cart-quantity">
                <button class="quantity__button no-js-hidden" name="minus" type="button">
                  <span class="visually-hidden">Decrease quantity</span>
                  <svg width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4 8h8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
                </button>
                <input
                  class="quantity__input"
                  type="number"
                  name="updates[]"
                  value="${item.quantity}"
                  min="1"
                  ${maxAttr}
                  step="1"
                  aria-label="Quantity"
                  id="Quantity-${index + 1}"
                  data-index="${index + 1}"
                  data-dealer-stock="${item.dealer_stock || 0}"
                >
                <button class="quantity__button no-js-hidden" name="plus" type="button">
                  <span class="visually-hidden">Increase quantity</span>
                  <svg width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8 4v8M4 8h8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
                </button>
              </quantity-input>
              <button type="button" class="button button--tertiary remove-item dealer-remove-small" data-variant-id="${item.variantId}" style="margin-left: 0.5em; padding: 0; background: none; border: none;">
                <span style="display: inline-flex; align-items: center; justify-content: center;">
                  <svg width="20" height="20" fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m2 0v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6h16zm-5 4v6m-4-6v6" stroke="#222" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                </span>
              </button>
            </div>
            ${etaMsg}
          </div>
        </td>
        <td class="cart-item__totals right">
          <div class="cart-item__price-wrapper">
            ${discount > 0 ? `
              <div class='cart-item__discounted-prices'>
                <s class='cart-item__old-price'>RM${(originalLineTotal / 100).toLocaleString(undefined, {minimumFractionDigits: 2})} MYR</s>
                <span class='cart-item__final-price'>RM${(lineTotal / 100).toLocaleString(undefined, {minimumFractionDigits: 2})} MYR</span>
              </div>
            ` : `
              <span class='price price--end'>RM${(originalLineTotal / 100).toLocaleString(undefined, {minimumFractionDigits: 2})} MYR</span>
            `}
          </div>
        </td>
      </tr>
    `;
  }

  async fetchCumulativeQuantities(customerId, variantIds, vendor = null, lavaGenie = false, cyberG = false, options = {}) {
    const API_BASE = 'https://us-central1-music-connoisseur-360.cloudfunctions.net/saveShopifyDraftOrder';
    const body = { customerId, variantIds };
    if (vendor) body.vendor = vendor;
    if (Array.isArray(options.customerTags)) body.customerTags = options.customerTags;
    if (options.enyaCollectionKey) body.enyaCollectionKey = options.enyaCollectionKey;
    if (lavaGenie) {
      // For LAVA GENIE, we'll use a special flag to indicate we want collection-based cumulative quantities
      body.lavaGenie = true;
      body.collectionBased = true;  // Add this flag to indicate collection-based lookup
    }
    if (cyberG) {
      // For Cyber-G, we'll use a special flag to indicate we want collection-based cumulative quantities
      body.cyberG = true;
      body.collectionBased = true;  // Add this flag to indicate collection-based lookup
    }
    
    // Create a unique key for this request
    const requestKey = `${vendor}-${lavaGenie}-${cyberG}-${options.enyaCollectionKey || 'none'}-${variantIds.join(',')}`;
    
    // Check if we can make this request
    if (!this.canMakeRequest(requestKey)) {
      this.showRateLimitMessage();
      // Return cached data or empty data to prevent errors
      return variantIds.reduce((acc, id) => {
        acc[id] = 0;
        return acc;
      }, {});
    }
    
    // Mark this request as pending
    this._pendingRequests.set(requestKey, true);
    this._lastRequestTime = Date.now();
    
    try {
      const response = await fetch(`${API_BASE}/dealer-cumulative-quantities`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      
      if (response.status === 429) {
        // Rate limited - show warning message
        this.showRateLimitMessage();
        // Return cached data or empty data
        return variantIds.reduce((acc, id) => {
          acc[id] = 0;
          return acc;
        }, {});
      }
      
      const data = await response.json();
      
      // Log cumulative quantities based on vendor
      if (vendor === 'Enya Music' && !cyberG) {
        //console.log('Cumulative quantities for Enya Music (excluding Cyber-G):', data[variantIds[0]] || 0);
      } else if (vendor === 'LAVA MUSIC' && lavaGenie) {
        const genieTotal = data[variantIds[0]] || 0;
        //console.log('Cumulative quantities for Lava Genie:', genieTotal);
        // Ensure all variant IDs get the same total for Lava Genie
        variantIds.forEach(id => {
          data[id] = genieTotal;
        });
      } else if (vendor === 'Enya Music' && cyberG) {
        const cyberGTotal = data[variantIds[0]] || 0;
       //console.log('Cumulative quantities for Cyber-G:', cyberGTotal);
        // Ensure all variant IDs get the same total for Cyber-G
        variantIds.forEach(id => {
          data[id] = cyberGTotal;
        });
      }
      
      return data;
    } catch (error) {
      console.error('Error fetching cumulative quantities:', error);
      // Return empty data on error
      return variantIds.reduce((acc, id) => {
        acc[id] = 0;
        return acc;
      }, {});
    } finally {
      // Remove from pending requests
      this._pendingRequests.delete(requestKey);
    }
  }

  async renderCart() {
    this.showLoading(); // Show loading overlay at the start
    
    try {
      const cart = this.getCart();
      let customerId = null;
      if (window.customer && window.customer.id) {
        customerId = window.customer.id;
      } else if (window.customerId) {
        customerId = window.customerId;
      }
      let vendorInCart = {};
      let cumulativeMap = {};
      let genieInCart = 0;
      let genieCumulative = 0;
      let cyberGInCart = 0;
      let cyberGCumulative = 0;
      if (!customerId) {
        cart.forEach(item => { item.cumulative_quantity = 0; });
      } else {
        const customerTags = this.getCustomerTags ? this.getCustomerTags() : [];
        const isDealerLiveMusic = Array.isArray(customerTags) && customerTags.includes('Dealer') && customerTags.includes('Live Music');

        const variantIds = cart.map(item => item.variantId);
        // Split cart into groups
        const lavaGenieItems = cart.filter(item => item.vendor === 'LAVA MUSIC' && item.title.toLowerCase().includes('genie'));
        const cyberGItems = cart.filter(item => item.vendor === 'Enya Music' && item.title.toLowerCase().includes('cyber-g'));
        const enyaItems = cart.filter(item => item.vendor === 'Enya Music' && !item.title.toLowerCase().includes('cyber-g'));
        const valetonItems = cart.filter(item => item.vendor === 'Valeton');
        const sonicakeItems = cart.filter(item => item.vendor === 'Sonicake');
        const sireItems = cart.filter(item => item.vendor === 'SIRE');
        const otherItems = cart.filter(item => !lavaGenieItems.includes(item) && !cyberGItems.includes(item) && !enyaItems.includes(item) && !valetonItems.includes(item) && !sonicakeItems.includes(item) && !sireItems.includes(item));

        // Prepare variantId lists for each group
        const lavaGenieVariantIds = lavaGenieItems.map(item => item.variantId);
        const cyberGVariantIds = cyberGItems.map(item => item.variantId);
        const enyaVariantIds = enyaItems.map(item => item.variantId);
        const valetonVariantIds = valetonItems.map(item => item.variantId);
        const sonicakeVariantIds = sonicakeItems.map(item => item.variantId);
        const sireVariantIds = sireItems.map(item => item.variantId);
        const otherVariantIds = otherItems.map(item => item.variantId);

        // Fetch cumulative quantities for each group
        let cumulativeMapLavaGenie = {};
        let cumulativeMapCyberG = {};
        let cumulativeMapEnya = {};
        let enyaCollectionCumulatives = {};
        let cumulativeMapValeton = {};
        let cumulativeMapSonicake = {};
        let cumulativeMapSire = {};
        let cumulativeMapOther = {};
        if (lavaGenieVariantIds.length > 0) {
          // For LAVA GENIE, we only need to fetch once and use the same value for all items
          cumulativeMapLavaGenie = await this.fetchCumulativeQuantities(customerId, [lavaGenieVariantIds[0]], 'LAVA MUSIC', true);
          // Create a map where all LAVA GENIE variants share the same cumulative quantity
          const genieCumulative = Object.values(cumulativeMapLavaGenie)[0] || 0;
          lavaGenieVariantIds.forEach(variantId => {
            cumulativeMapLavaGenie[variantId] = genieCumulative;
          });
        }
        if (cyberGVariantIds.length > 0) {
          // For Cyber-G, we only need to fetch once and use the same value for all items
          cumulativeMapCyberG = await this.fetchCumulativeQuantities(customerId, [cyberGVariantIds[0]], 'Enya Music', false, true);
          // Create a map where all Cyber-G variants share the same cumulative quantity
          const cyberGCumulative = Object.values(cumulativeMapCyberG)[0] || 0;
          cyberGVariantIds.forEach(variantId => {
            cumulativeMapCyberG[variantId] = cyberGCumulative;
          });
        }
        if (enyaVariantIds.length > 0) {
          if (isDealerLiveMusic) {
            // Keep existing vendor-based cumulative for ENYA products not in the 5 specified collections.
            const nonScopedVariantIds = enyaItems
              .filter(item => !getEnyaLiveMusicCollectionKey(item.collections || []))
              .map(item => item.variantId);
            if (nonScopedVariantIds.length > 0) {
              cumulativeMapEnya = await this.fetchCumulativeQuantities(
                customerId,
                nonScopedVariantIds,
                'Enya Music',
                false,
                false,
                { customerTags }
              );
            }

            // Fetch collection-scoped cumulative once per collection (main products only)
            const collectionsInCart = Array.from(
              new Set(
                enyaItems
                  .filter(item => item.productType?.toLowerCase() !== 'accessories')
                  .map(item => getEnyaLiveMusicCollectionKey(item.collections || []))
                  .filter(Boolean)
              )
            );

            for (const collectionKey of collectionsInCart) {
              const resp = await this.fetchCumulativeQuantities(
                customerId,
                [enyaVariantIds[0]],
                'Enya Music',
                false,
                false,
                { customerTags, enyaCollectionKey: collectionKey }
              );
              enyaCollectionCumulatives[collectionKey] = Object.values(resp)[0] || 0;
            }
          } else {
            cumulativeMapEnya = await this.fetchCumulativeQuantities(customerId, enyaVariantIds, 'Enya Music', false);
          }
        }
        if (valetonVariantIds.length > 0) {
          cumulativeMapValeton = await this.fetchCumulativeQuantities(customerId, valetonVariantIds, 'Valeton', false);
        }
        if (sonicakeVariantIds.length > 0) {
          cumulativeMapSonicake = await this.fetchCumulativeQuantities(customerId, sonicakeVariantIds, 'Sonicake', false);
        }
        if (sireVariantIds.length > 0) {
          cumulativeMapSire = await this.fetchCumulativeQuantities(customerId, sireVariantIds, 'SIRE', false);
        }
        if (otherVariantIds.length > 0) {
          cumulativeMapOther = await this.fetchCumulativeQuantities(customerId, otherVariantIds, null, false);
        }
        // Merge all maps
        cumulativeMap = {
          ...cumulativeMapLavaGenie,
          ...cumulativeMapCyberG,
          ...cumulativeMapEnya,
          ...cumulativeMapValeton,
          ...cumulativeMapSonicake,
          ...cumulativeMapSire,
          ...cumulativeMapOther
        };

        // EFNOTE Electronic Drum Kits: shared MOQ across the collection
        const efnoteKitsItems = cart.filter(
          (item) => item.vendor === 'EFNOTE' && isInCollection(item.collections || [], EFNOTE_ELECTRONIC_DRUM_KITS_COLLECTION_TITLE)
        );
        const efnoteKitsInCart = efnoteKitsItems.reduce((sum, item) => sum + (parseInt(item.quantity, 10) || 0), 0);
        const efnoteKitsCumulative = efnoteKitsItems.reduce(
          (sum, item) => sum + (parseInt(cumulativeMap[item.variantId], 10) || 0),
          0
        );

        // Vendor-level logic for Enya, Valeton, Sonicake, SIRE
        const vendorVariantIds = {
          'Enya Music': enyaItems.map(item => item.variantId),
          'Valeton': valetonItems.map(item => item.variantId),
          'Sonicake': sonicakeItems.map(item => item.variantId),
          'SIRE': sireItems.map(item => item.variantId)
        };
        const vendorCumulatives = {
          'Enya Music': vendorVariantIds['Enya Music'].length > 0 ? (cumulativeMap[vendorVariantIds['Enya Music'][0]] || 0) : 0,
          'Valeton': vendorVariantIds['Valeton'].length > 0 ? (cumulativeMap[vendorVariantIds['Valeton'][0]] || 0) : 0,
          'Sonicake': vendorVariantIds['Sonicake'].length > 0 ? (cumulativeMap[vendorVariantIds['Sonicake'][0]] || 0) : 0,
          'SIRE': vendorVariantIds['SIRE'].length > 0 ? (cumulativeMap[vendorVariantIds['SIRE'][0]] || 0) : 0
        };

        // Sum in-cart quantities per vendor (excluding accessories for ENYA Music)
        vendorInCart = {};
        cart.forEach(item => {
          if (["Enya Music", "Valeton", "Sonicake", "SIRE"].includes(item.vendor)) {
            // For ENYA Music, exclude accessories from vendor quantity calculations
            if (item.vendor === 'Enya Music' && item.productType?.toLowerCase() === 'accessories') {
              // Skip accessories for ENYA Music vendor quantity calculation
              console.log('[DEALER CART DEBUG] Skipping accessory for vendor quantity calculation:', {
                title: item.title,
                productType: item.productType,
                vendor: item.vendor
              });
              return;
            }
            vendorInCart[item.vendor] = vendorInCart[item.vendor] || 0;
            vendorInCart[item.vendor] += item.quantity;
          }
        });

        // LAVA GENIE logic
        const genieVariantIds = lavaGenieItems.map(item => String(item.variantId));
        genieInCart = genieVariantIds.reduce((sum, variantId) => {
          const item = cart.find(i => String(i.variantId) === variantId);
          return sum + (item ? item.quantity : 0);
        }, 0);
        
        // Get cumulative quantity for LAVA GENIE collection
        const singleGenieItem = cart.find(item => item.vendor === 'LAVA MUSIC' && item.title.toLowerCase().includes('genie'));
        if (singleGenieItem) {
          // Fetch cumulative quantities for LAVA GENIE using the current item's variant ID
          const genieCumulativeMap = await this.fetchCumulativeQuantities(customerId, [singleGenieItem.variantId], 'LAVA MUSIC', true);
          genieCumulative = Object.values(genieCumulativeMap)[0] || 0;
          //console.log('Lava Genie cumulative quantity:', genieCumulative);
        } else {
          genieCumulative = 0;
        }

        // Cyber-G logic
        cyberGInCart = cyberGVariantIds.reduce((sum, variantId) => {
          const item = cart.find(i => String(i.variantId) === variantId);
          return sum + (item ? item.quantity : 0);
        }, 0);
        
        // Get cumulative quantity for Cyber-G collection
        const singleCyberGItem = cart.find(item => item.vendor === 'Enya Music' && item.title.toLowerCase().includes('cyber-g'));
        if (singleCyberGItem) {
          // Fetch cumulative quantities for Cyber-G using the current item's variant ID
          const cyberGCumulativeMap = await this.fetchCumulativeQuantities(customerId, [singleCyberGItem.variantId], 'Enya Music', false, true);
          cyberGCumulative = Object.values(cyberGCumulativeMap)[0] || 0;
          //console.log('Cyber-G cumulative quantity:', cyberGCumulative);
        } else {
          cyberGCumulative = 0;
        }

        // Calculate total Enya in cart and cumulative (vendor-level)
        const enyaInCart = vendorVariantIds['Enya Music'].reduce((sum, variantId) => {
          const item = cart.find(i => String(i.variantId) === String(variantId));
          // Exclude accessories from ENYA Music quantity calculations
          if (item && item.productType?.toLowerCase() === 'accessories') {
            return sum; // Don't add accessories to the total
          }
          return sum + (item ? item.quantity : 0);
        }, 0);
        const enyaCumulative = vendorVariantIds['Enya Music'].length > 0 ? (cumulativeMap[vendorVariantIds['Enya Music'][0]] || 0) : 0;
        
        //console.log('Regular Enya Music cumulative quantity:', enyaCumulative);
        //console.log('Regular Enya Music in cart:', enyaInCart);

        // Assign cumulative_quantity and totals for each item
        cart.forEach(item => {
          if (item.vendor === 'Enya Music' && !item.title.toLowerCase().includes('cyber-g')) {
            // Regular Enya Music products (excluding Cyber-G)
            item.cumulative_quantity = enyaCumulative;
            item.total_vendor_qty = enyaInCart + enyaCumulative;

            // Dealer + Live Music: collection-scoped totals for 5 ENYA collections
            if (isDealerLiveMusic) {
              const key = getEnyaLiveMusicCollectionKey(item.collections || []);
              if (key) {
                const inCartCollectionQty = cart
                  .filter(i =>
                    i.vendor === 'Enya Music' &&
                    !i.title.toLowerCase().includes('cyber-g') &&
                    i.productType?.toLowerCase() !== 'accessories' &&
                    getEnyaLiveMusicCollectionKey(i.collections || []) === key
                  )
                  .reduce((sum, i) => sum + (parseInt(i.quantity, 10) || 0), 0);
                const cumulativeCollectionQty = enyaCollectionCumulatives[key] || 0;
                item.total_enya_collection_qty = inCartCollectionQty + cumulativeCollectionQty;
              }
            }

          } else if (item.vendor === 'Enya Music' && item.title.toLowerCase().includes('cyber-g')) {
            // For Cyber-G items, always use the same cumulative quantity regardless of variant
            item.cumulative_quantity = cyberGCumulative;
            // Calculate total_cyber_g_qty based on all Cyber-G items in cart
            const allCyberGItems = cart.filter(i => i.vendor === 'Enya Music' && i.title.toLowerCase().includes('cyber-g'));
            const totalCyberGInCart = allCyberGItems.reduce((sum, i) => sum + i.quantity, 0);
            item.total_cyber_g_qty = totalCyberGInCart + cyberGCumulative;

          } else if (["Valeton", "Sonicake", "SIRE"].includes(item.vendor)) {
            item.cumulative_quantity = vendorCumulatives[item.vendor];
            item.total_vendor_qty = vendorInCart[item.vendor] + vendorCumulatives[item.vendor];

          } else if (item.vendor === 'LAVA MUSIC' && item.title.toLowerCase().includes('genie')) {
            // For LAVA GENIE items, always use the same cumulative quantity regardless of variant
            item.cumulative_quantity = genieCumulative;
            // Calculate total_genie_qty based on all LAVA GENIE items in cart
            const allGenieItems = cart.filter(i => i.vendor === 'LAVA MUSIC' && i.title.toLowerCase().includes('genie'));
            const totalGenieInCart = allGenieItems.reduce((sum, i) => sum + i.quantity, 0);
            item.total_genie_qty = totalGenieInCart + genieCumulative;

          } else if (item.vendor === 'EFNOTE' && isInCollection(item.collections || [], EFNOTE_ELECTRONIC_DRUM_KITS_COLLECTION_TITLE)) {
            item.cumulative_quantity = cumulativeMap[item.variantId] || 0;
            item.total_efnote_kits_qty = efnoteKitsInCart + efnoteKitsCumulative;

          } else {
            item.cumulative_quantity = cumulativeMap[item.variantId] || 0;

          }
        });

        // Now set the cart, so getDealerDiscount uses the correct values
        this.setCart(cart);
      }

      // Fetch dealer stock for all items in parallel
      const dealerStocks = await Promise.all(cart.map(item =>
        fetchDealerStock(item.variantId, dealerLocations)
      ));
      // Attach latest dealer stock to each item
      cart.forEach((item, i) => {
        item.latestDealerStock = dealerStocks[i];
      });

      if (!this.cartItemsContainer) return;

      this.cartItemsContainer.innerHTML = cart.map((item, i) => {
        const html = this.getDealerCartRowHTML(item, i);
        return html;
      }).join('');
      
      if (cart.length === 0) {
        if (this.cartWarnings) this.cartWarnings.style.display = 'block';
        if (this.cartContents) this.cartContents.style.display = 'none';
      } else {
        if (this.cartWarnings) this.cartWarnings.style.display = 'none';
        if (this.cartContents) this.cartContents.style.display = 'block';
      }
      
      // Now that all cumulative quantities are calculated, recalculate discounts and save to localStorage
      this.setCart(cart, true);
      
      this.attachEventListeners();
      if (typeof window.updateDealerCartTotals === 'function') window.updateDealerCartTotals();
      
    } catch (error) {
      console.error('Error rendering cart:', error);
      throw error; // Re-throw to ensure Promise rejection
    } finally {
      this.hideLoading(); // Hide loading overlay when done
    }
  }

  attachEventListeners() {
    // Remove any previous listeners to avoid duplicates
    if (this._delegatedListener) {
      this.cartItemsContainer.removeEventListener('click', this._delegatedListener);
      this.cartItemsContainer.removeEventListener('change', this._delegatedChangeListener);
    }

    // Delegated click for plus/minus and remove
    this._delegatedListener = (e) => {
      if (this._isUpdating) return;  // Prevent handling if update is in progress
      
      const plusMinusBtn = e.target.closest('.quantity__button');
      if (plusMinusBtn) {
        // Log which button was clicked and which variant
        const cartItem = plusMinusBtn.closest('.cart-item');
        const variantId = cartItem ? cartItem.dataset.variantId : 'unknown';
        // console.log(`[DELEGATED CLICK] ${plusMinusBtn.name} button for variant ${variantId}`);
        this.handleQuantityButton(e);
        return;
      }
      const removeBtn = e.target.closest('.dealer-remove-small');
      if (removeBtn) {
        this.handleRemove(e);
        return;
      }
    };
    this.cartItemsContainer.addEventListener('click', this._delegatedListener);

    // Delegated change for quantity input
    this._delegatedChangeListener = (e) => {
      if (this._isUpdating) return;  // Prevent handling if update is in progress
      
      if (e.target.classList.contains('quantity__input')) {
        // console.log(`[DELEGATED CHANGE] Quantity input for variant ${e.target.closest('.cart-item').dataset.variantId} changed to ${e.target.value}`);
        this.handleQuantityChange(e);
      }
    };
    this.cartItemsContainer.addEventListener('change', this._delegatedChangeListener);
  }

  handleQuantityButton(e) {
    e.preventDefault();
    if (this._isUpdating) return;  // Prevent handling if update is in progress
    
    const button = e.target.closest('button');
    if (!button) return;

    // Find the closest cart-item row, then the input inside it
    const cartItem = button.closest('.cart-item');
    if (!cartItem) return;
    
    const variantId = cartItem.dataset.variantId;
    
    // Check if we can make a request (rate limiting)
    if (!this.canMakeRequest(variantId)) {
      this.showLoading();
      this.showRateLimitMessage();
      setTimeout(() => {
        this.hideLoading();
        this.resetLoadingMessage();
      }, 2000); // Show rate limit message for 2 seconds
      return;
    }
    
    this._isUpdating = true;  // Set flag to prevent other handlers
    this.showLoading(); // Show loading overlay immediately
    this.resetLoadingMessage(); // Ensure normal message is shown
    
    const startTime = Date.now();
    const minLoadingTime = 500; // Minimum loading time in milliseconds
    
    let cart = JSON.parse(JSON.stringify(this.getCart())); // Deep copy
    const itemIndex = cart.findIndex(item => String(item.variantId) === String(variantId));
    
    if (itemIndex === -1) {
      this._isUpdating = false;
      this.hideLoading();
      return;
    }

    // Always use the value from the cart object, not the DOM input
    let currentValue = cart[itemIndex].quantity || 1;
    const dealerStock = parseInt(cart[itemIndex].dealer_stock, 10) || 0;
    let newValue = currentValue;

    if (button.name === 'plus') {
      newValue = currentValue + 1;
      // console.log(`[PLUS] Variant ${variantId}: ${currentValue} -> ${newValue}`);
    } else if (button.name === 'minus') {
      if (currentValue <= 1) {
        this._isUpdating = false;
        this.hideLoading();
        return;
      }
      newValue = currentValue - 1;
      // console.log(`[MINUS] Variant ${variantId}: ${currentValue} -> ${newValue}`);
    }

    // Only apply dealer stock limit for non-dealers
    const isDealer = this.getCustomerTags && this.getCustomerTags().includes('Dealer');
    if (!isDealer && dealerStock > 0 && newValue > dealerStock) {
      newValue = dealerStock;
    }

    // Update the cart with the new value
    cart[itemIndex].quantity = newValue;
    
    // Save cart to localStorage without recalculating discounts yet
    try {
      if (cart.length === 0) {
        localStorage.removeItem(this.cartKey);
      } else {
        localStorage.setItem(this.cartKey, JSON.stringify(cart));
      }
    } catch (e) {
      console.error('Error saving cart to localStorage', e);
    }

    // Re-render the cart (this will recalculate cumulative quantities and discounts)
    this.renderCart().then(() => {
      // Ensure minimum loading time
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, minLoadingTime - elapsed);
      
      setTimeout(() => {
        this._isUpdating = false;
      }, remaining);
    }).catch(() => {
      this._isUpdating = false;
    });
  }

  handleQuantityChange(e) {
    if (this._isUpdating) return;  // Prevent handling if update is in progress
    
    const input = e.target;
    const cartItem = input.closest('.cart-item');
    const variantId = cartItem.dataset.variantId;
    
    // Check if we can make a request (rate limiting)
    if (!this.canMakeRequest(variantId)) {
      this.showLoading();
      this.showRateLimitMessage();
      setTimeout(() => {
        this.hideLoading();
        this.resetLoadingMessage();
      }, 2000); // Show rate limit message for 2 seconds
      return;
    }
    
    this._isUpdating = true;  // Set flag to prevent other handlers
    this.showLoading(); // Show loading overlay immediately
    this.resetLoadingMessage(); // Ensure normal message is shown
    
    const startTime = Date.now();
    const minLoadingTime = 500; // Minimum loading time in milliseconds
    
    let newQuantity = parseInt(input.value, 10) || 1;
    
    if (newQuantity < 1) newQuantity = 1;
    
    // Only apply dealer stock limit for non-dealers
    const isDealer = this.getCustomerTags && this.getCustomerTags().includes('Dealer');
    const dealerStock = parseInt(input.dataset.dealerStock, 10) || 0;
    
    if (!isDealer && dealerStock > 0 && newQuantity > dealerStock) {
      newQuantity = dealerStock;
      input.value = dealerStock; // Update input to reflect the limit
    }
    
    let cart = JSON.parse(JSON.stringify(this.getCart())); // Deep copy
    const itemIndex = cart.findIndex(item => String(item.variantId) === String(variantId));
    
    if (itemIndex !== -1) {
      // Only update if the quantity actually changed
      if (cart[itemIndex].quantity !== newQuantity) {
        cart[itemIndex].quantity = newQuantity;
        
        // Save cart to localStorage without recalculating discounts yet
        try {
          if (cart.length === 0) {
            localStorage.removeItem(this.cartKey);
          } else {
            localStorage.setItem(this.cartKey, JSON.stringify(cart));
          }
        } catch (e) {
          console.error('Error saving cart to localStorage', e);
        }
        
        // Re-render the cart (this will recalculate cumulative quantities and discounts)
        this.renderCart().then(() => {
          // Ensure minimum loading time
          const elapsed = Date.now() - startTime;
          const remaining = Math.max(0, minLoadingTime - elapsed);
          
          setTimeout(() => {
            this._isUpdating = false;
          }, remaining);
        }).catch(() => {
          this._isUpdating = false;
        });
      } else {
        // If quantity didn't change, hide loading immediately
        this.hideLoading();
        this._isUpdating = false;
      }
    } else {
      // If item not found, hide loading immediately
      this.hideLoading();
      this._isUpdating = false;
    }

    // console.log('handleQuantityChange: newQuantity', newQuantity, 'isDealer', isDealer, 'dealerStock', dealerStock);
  }

  handleRemove(e) {
    e.preventDefault();
    
    const button = e.target.closest('button');
    if (!button) return;
    
    const cartItem = button.closest('[data-variant-id]');
    if (!cartItem) return;
    
    const variantId = cartItem.dataset.variantId;
    
    // Check if we can make a request (rate limiting)
    if (!this.canMakeRequest(variantId)) {
      this.showLoading();
      this.showRateLimitMessage();
      setTimeout(() => {
        this.hideLoading();
        this.resetLoadingMessage();
      }, 2000); // Show rate limit message for 2 seconds
      return;
    }
    
    this.showLoading(); // Show loading overlay immediately
    this.resetLoadingMessage(); // Ensure normal message is shown
    
    const startTime = Date.now();
    const minLoadingTime = 500; // Minimum loading time in milliseconds
    
    let cart = this.getCart();
    
    // Filter out the item to remove
    const newCart = cart.filter(item => String(item.variantId) !== String(variantId));
    
    // Save cart to localStorage without recalculating discounts yet
    try {
      if (newCart.length === 0) {
        localStorage.removeItem(this.cartKey);
      } else {
        localStorage.setItem(this.cartKey, JSON.stringify(newCart));
      }
    } catch (e) {
      console.error('Error saving cart to localStorage', e);
    }
    
    // Re-render the cart (this will recalculate cumulative quantities and discounts)
    this.renderCart().then(() => {
      // Ensure minimum loading time
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, minLoadingTime - elapsed);
      
      setTimeout(() => {
        // No need to reset _isUpdating here since remove doesn't use it
      }, remaining);
    }).catch(() => {
      // Handle error if needed
    });
    
    // console.log('Item removed. Current cart:', this.getCart());
  }

  getCustomerTags() {
    return window.customerTags || [];
  }
}

customElements.define('dealer-cart-items', DealerCartItems);

// Dealer session management
let dealerSessionCheckInterval;
let lastDealerCheck = 0;
const DEALER_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes
const DEALER_CHECK_THRESHOLD = 10 * 60 * 1000; // 10 minutes

function startDealerSessionCheck(customerId) {
  // Store customer ID in sessionStorage
  if (customerId) {
    sessionStorage.setItem('dealerCustomerId', customerId);
  }

  // Clear any existing interval
  if (dealerSessionCheckInterval) {
    clearInterval(dealerSessionCheckInterval);
  }

  // Initial check
  checkDealerSession(customerId);

  // Set up periodic check
  dealerSessionCheckInterval = setInterval(() => {
    const storedCustomerId = sessionStorage.getItem('dealerCustomerId');
    if (storedCustomerId) {
      checkDealerSession(storedCustomerId);
    }
  }, DEALER_CHECK_INTERVAL);

  // Add visibility change listener to check session when tab becomes visible
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      const now = Date.now();
      if (now - lastDealerCheck > DEALER_CHECK_THRESHOLD) {
        const storedCustomerId = sessionStorage.getItem('dealerCustomerId');
        if (storedCustomerId) {
          checkDealerSession(storedCustomerId);
        }
      }
    }
  });
}

async function checkDealerSession(customerId) {
  try {
    lastDealerCheck = Date.now();
    const response = await fetch(`https://us-central1-music-connoisseur-360.cloudfunctions.net/saveShopifyDraftOrder/dealer-session-check?customerId=${customerId}`);
    const data = await response.json();

    if (!data.success || !data.isDealer) {
      // Clear stored customer ID
      sessionStorage.removeItem('dealerCustomerId');
      
      // If dealer status is lost, redirect to login
      window.location.href = '/account/login?return_to=' + encodeURIComponent(window.location.pathname);
      return;
    }

    // Update any UI elements that depend on dealer status
    updateDealerUI(data.customer);
  } catch (error) {
    console.error('Error checking dealer session:', error);
    // Don't clear session on network errors
  }
}

function updateDealerUI(customer) {
  // Update dealer badge if it exists
  const dealerBadge = document.querySelector('.dealer-badge');
  if (dealerBadge) {
    dealerBadge.style.display = customer.tags.includes('Dealer') ? 'inline-block' : 'none';
  }

  // Update any other dealer-specific UI elements
  const dealerElements = document.querySelectorAll('[data-dealer-only]');
  dealerElements.forEach(element => {
    element.style.display = customer.tags.includes('Dealer') ? '' : 'none';
  });
}

// Initialize dealer session check when customer data is available
document.addEventListener('DOMContentLoaded', function() {
  // Try to get customer ID from the page
  const customerDataElement = document.getElementById('customer-data');
  let customerId = null;

  if (customerDataElement) {
    try {
      // Read from data attributes instead of textContent
      customerId = customerDataElement.dataset.customerId;
      const customerTags = customerDataElement.dataset.customerTags;
      const isDealer = customerDataElement.dataset.isDealer;
      
      // Set global variables for use in other parts of the code
      if (customerTags) {
        try {
          // Check if customerTags is a non-empty string before parsing
          if (typeof customerTags === 'string' && customerTags.trim() !== '') {
            window.customerTags = JSON.parse(customerTags);
          } else {
            window.customerTags = [];
          }
        } catch (e) {
          console.error('Error parsing customer tags:', e);
          window.customerTags = [];
        }
      } else {
        // If customerTags is empty or undefined, default to empty array
        window.customerTags = [];
      }
      
      if (isDealer) {
        try {
          // The Liquid template now outputs "true" or "false" as strings
          window.isDealer = isDealer === 'true';
        } catch (e) {
          console.error('Error parsing dealer status:', e);
          window.isDealer = false;
        }
      } else {
        // If isDealer is empty or undefined, default to false
        window.isDealer = false;
      }
    } catch (e) {
      console.error('Error reading customer data:', e);
    }
  } else {
    // Set default values when no customer data element exists
    window.customerTags = [];
    window.isDealer = false;
  }

  // Fallback: Check for global variables set by dealer cart items section
  if (!customerId && window.customerId) {
    customerId = window.customerId;
  }
  
  // If no customer ID in page, try to get from sessionStorage
  if (!customerId) {
    customerId = sessionStorage.getItem('dealerCustomerId');
  }

  if (customerId) {
    startDealerSessionCheck(customerId);
  }
});